import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  assignAdminUserToMe,
  fetchAdminUsers,
  setAdminUserAssignment,
  unassignAdminUser,
} from '../api/auth';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './AdminTeamPage.module.css';

function toDate(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale || undefined);
}

function Metric({ label, value }) {
  return (
    <div className={styles.metricCard}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function AdminTeamPage({ currentUser }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isSuperadmin = currentUser?.role === 'admin' && currentUser?.isSuperadmin === true;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [assignmentDrafts, setAssignmentDrafts] = useState({});

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchAdminUsers();
      const nextUsers = Array.isArray(result.users) ? result.users : [];
      setUsers(nextUsers);
      setAssignmentDrafts(
        Object.fromEntries(
          nextUsers
            .filter((user) => user.role === 'normal')
            .map((user) => [user.id, user.assignedAdminUserId ? String(user.assignedAdminUserId) : ''])
        )
      );
    } catch (err) {
      setError(err.message || t('Unable to load admin teams.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadUsers();
  }, [currentUser?.role, loadUsers, navigate]);

  const admins = useMemo(
    () => users.filter((user) => user.role === 'admin' && user.isActive),
    [users]
  );
  const delegatedAdmins = useMemo(
    () => admins.filter((user) => Number(user.id) !== Number(currentUser?.id)),
    [admins, currentUser?.id]
  );
  const normalUsers = useMemo(
    () => users.filter((user) => user.role === 'normal'),
    [users]
  );
  const unassignedUsers = useMemo(
    () => normalUsers.filter((user) => !user.assignedAdminUserId),
    [normalUsers]
  );
  const myAssignedUsers = useMemo(
    () => normalUsers.filter((user) => Number(user.assignedAdminUserId) === Number(currentUser?.id)),
    [currentUser?.id, normalUsers]
  );

  const groups = useMemo(() => (
    delegatedAdmins.map((admin) => ({
      admin,
      users: normalUsers.filter((user) => Number(user.assignedAdminUserId) === Number(admin.id)),
    }))
  ), [delegatedAdmins, normalUsers]);

  const updateAssignmentDraft = (userId, value) => {
    setAssignmentDrafts((current) => ({ ...current, [userId]: value }));
  };

  const handleSetAssignment = async (userId) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const draft = assignmentDrafts[userId] || '';
      const result = await setAdminUserAssignment(userId, draft ? Number(draft) : null);
      setMessage(t(result.message || 'Assignment updated.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to update assignment.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignToMe = async (userId) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const result = await assignAdminUserToMe(userId);
      setMessage(t(result.message || 'User assigned successfully.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to claim this user.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (userId) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const result = await unassignAdminUser(userId);
      setMessage(t(result.message || 'Assignment removed.'));
      await loadUsers();
    } catch (err) {
      setError(err.message || t('Unable to release this user.'));
    } finally {
      setSaving(false);
    }
  };

  const renderUserCard = (user, { showAssignToMe = false, showRelease = false, showSuperadminAssignment = false } = {}) => (
    <article key={user.id} className={styles.userCard}>
      <div className={styles.userTop}>
        <div>
          <strong>{user.username}</strong>
          <p>{user.fullName || user.email || '—'}</p>
        </div>
        <span className={`${styles.badge} ${user.isActive ? styles.activeBadge : styles.inactiveBadge}`}>
          {user.isActive ? t('Active') : t('Disabled')}
        </span>
      </div>

      <div className={styles.userMeta}>
        <span>{t('Company')}: {user.company || '-'}</span>
        <span>{t('Request type')}: {user.requestType || '-'}</span>
        <span>{t('License')}: {user.requiresLicenseAcceptance ? t('Required') : t('Not required')}</span>
        <span>{t('Created')}: {toDate(user.createdAt, i18n.language)}</span>
      </div>

      {showSuperadminAssignment ? (
        <div className={styles.assignmentRow}>
          <select
            value={assignmentDrafts[user.id] || ''}
            onChange={(event) => updateAssignmentDraft(user.id, event.target.value)}
            className={styles.select}
          >
            <option value="">{t('Unassigned pool')}</option>
            {delegatedAdmins.map((admin) => (
              <option key={admin.id} value={String(admin.id)}>
                {admin.username}
              </option>
            ))}
          </select>
          <button type="button" className={styles.secondaryButton} onClick={() => handleSetAssignment(user.id)} disabled={saving}>
            {t('Apply')}
          </button>
        </div>
      ) : null}

      <div className={styles.cardActions}>
        <button type="button" className={styles.secondaryButton} onClick={() => navigate(`/admin/users/${user.id}`)}>
          {t('Open detail')}
        </button>
        {showAssignToMe ? (
          <button type="button" className={styles.primaryButton} onClick={() => handleAssignToMe(user.id)} disabled={saving}>
            {t('Assign to me')}
          </button>
        ) : null}
        {showRelease ? (
          <button type="button" className={styles.secondaryButton} onClick={() => handleUnassign(user.id)} disabled={saving}>
            {t('Release')}
          </button>
        ) : null}
      </div>
    </article>
  );

  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <main className={styles.container}>
      <GoToDashboardButton />

      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t('Administration')}</p>
          <h1 className={styles.title}>{t('Admin Team')}</h1>
          <p className={styles.subtitle}>
            {isSuperadmin
              ? t('See every delegated admin, route users to the right owner, and keep the company workspace organized.')
              : t('See your working group, claim unassigned users, and keep your assigned users moving.')}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={() => navigate('/admin')}>
            {t('Back to Admin Panel')}
          </button>
          <button type="button" className={styles.primaryButton} onClick={loadUsers} disabled={loading || saving}>
            {loading ? t('Loading...') : t('Refresh')}
          </button>
        </div>
      </header>

      <section className={styles.metricsGrid}>
        <Metric label={t('Delegated admins')} value={delegatedAdmins.length} />
        <Metric label={t('Normal users')} value={normalUsers.length} />
        <Metric label={isSuperadmin ? t('Unassigned pool') : t('Available to claim')} value={unassignedUsers.length} />
        <Metric label={isSuperadmin ? t('Largest team') : t('Assigned to me')} value={isSuperadmin ? Math.max(0, ...groups.map((group) => group.users.length)) : myAssignedUsers.length} />
      </section>

      {(message || error) ? (
        <div className={`${styles.notice} ${error ? styles.error : styles.success}`}>
          {error || message}
        </div>
      ) : null}

      {loading ? (
        <section className={styles.section}>
          <div className={styles.emptyState}>{t('Loading admin team view...')}</div>
        </section>
      ) : (
        <>
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div>
                <h2>{isSuperadmin ? t('Unassigned pool') : t('Users available to claim')}</h2>
                <p>
                  {isSuperadmin
                    ? t('Users here are not owned by any delegated admin yet. Assign them directly from this list.')
                    : t('These users are not owned by any delegated admin yet. Claim them when they belong to your group.')}
                </p>
              </div>
            </div>
            {unassignedUsers.length ? (
              <div className={styles.userGrid}>
                {unassignedUsers.map((user) => renderUserCard(user, {
                  showAssignToMe: !isSuperadmin,
                  showSuperadminAssignment: isSuperadmin,
                }))}
              </div>
            ) : (
              <div className={styles.emptyState}>{t('No users are currently waiting in the unassigned pool.')}</div>
            )}
          </section>

          {isSuperadmin ? (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div>
                  <h2>{t('Delegated admin teams')}</h2>
                  <p>{t('Each column shows the users currently owned by that admin. Reassign people directly where the work really lives.')}</p>
                </div>
              </div>
              {groups.length ? (
                <div className={styles.groupGrid}>
                  {groups.map((group) => (
                    <section key={group.admin.id} className={styles.groupCard}>
                      <div className={styles.groupHeader}>
                        <div>
                          <span>{t('Delegated admin')}</span>
                          <h3>{group.admin.username}</h3>
                          <p>{group.admin.fullName || group.admin.email || '—'}</p>
                        </div>
                        <strong>{t('{{count}} users', { count: group.users.length })}</strong>
                      </div>
                      {group.users.length ? (
                        <div className={styles.groupUsers}>
                          {group.users.map((user) => renderUserCard(user, { showSuperadminAssignment: true }))}
                        </div>
                      ) : (
                        <div className={styles.emptyState}>{t('No users assigned to this admin yet.')}</div>
                      )}
                    </section>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>{t('No delegated admins exist yet.')}</div>
              )}
            </section>
          ) : (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <div>
                  <h2>{t('My team')}</h2>
                  <p>{t('These are the normal users currently assigned to your working group.')}</p>
                </div>
              </div>
              {myAssignedUsers.length ? (
                <div className={styles.userGrid}>
                  {myAssignedUsers.map((user) => renderUserCard(user, { showRelease: true }))}
                </div>
              ) : (
                <div className={styles.emptyState}>{t('No users are assigned to you yet.')}</div>
              )}
            </section>
          )}
        </>
      )}
    </main>
  );
}
